import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { loadGoogleMapsPlaces } from '../../lib/googleMapsLoader';

/**
 * useGoogleMaps
 *
 * Idempotent hook that loads the Google Maps JS API (with `places` library)
 * and exposes a `{ google, error, loading }` shape. Reuses the global loader
 * promise so multiple consumers (Build Day, Map View, Places fields) share
 * a single script tag.
 */
export function useGoogleMaps() {
  const [state, setState] = useState({
    google: typeof window !== 'undefined' ? window.google || null : null,
    loading: !(typeof window !== 'undefined' && window.google?.maps?.places),
    error: null,
  });

  useEffect(() => {
    let alive = true;
    loadGoogleMapsPlaces()
      .then((google) => {
        if (alive) setState({ google, loading: false, error: null });
      })
      .catch((err) => {
        if (alive) setState({ google: null, loading: false, error: err });
      });
    return () => { alive = false; };
  }, []);

  return state;
}

/**
 * useDirectionsCache
 *
 * In-memory + reusable cache for Google Directions Service results keyed by
 * a stable hash of the ordered stops. Returns:
 *   • `getDirections(stops, options)` — async; resolves to a
 *     google.maps.DirectionsResult, leveraging an internal Map cache.
 *
 * Polylines are computed on the client (one request per technician route)
 * to avoid round-tripping turn-by-turn geometry through the backend.
 */
export function useDirectionsCache(google) {
  const cacheRef = useRef(new Map());
  const serviceRef = useRef(null);

  const getDirections = useCallback(async (waypoints) => {
    if (!google?.maps) throw new Error('Google Maps not loaded');
    if (!serviceRef.current) {
      serviceRef.current = new google.maps.DirectionsService();
    }
    if (!waypoints || waypoints.length < 2) return null;
    const key = JSON.stringify(waypoints.map((p) => [
      Number(p.lat).toFixed(5),
      Number(p.lng).toFixed(5),
    ]));
    if (cacheRef.current.has(key)) return cacheRef.current.get(key);

    const origin = waypoints[0];
    const destination = waypoints[waypoints.length - 1];
    const mid = waypoints.slice(1, -1).map((p) => ({
      location: new google.maps.LatLng(p.lat, p.lng),
      stopover: true,
    }));

    return new Promise((resolve, reject) => {
      serviceRef.current.route(
        {
          origin: new google.maps.LatLng(origin.lat, origin.lng),
          destination: new google.maps.LatLng(destination.lat, destination.lng),
          waypoints: mid,
          travelMode: google.maps.TravelMode.DRIVING,
          drivingOptions: {
            departureTime: new Date(),
            trafficModel: google.maps.TrafficModel.BEST_GUESS,
          },
        },
        (result, status) => {
          if (status === 'OK' && result) {
            cacheRef.current.set(key, result);
            resolve(result);
          } else {
            reject(new Error(`Directions failed: ${status}`));
          }
        },
      );
    });
  }, [google]);

  return { getDirections };
}

/**
 * useMapBounds — compute a LatLngBounds object that fits every stop on screen.
 * Returns null when no valid points are available so callers can skip the
 * fitBounds() call entirely.
 */
export function useMapBounds(google, points) {
  return useMemo(() => {
    if (!google?.maps || !points || points.length === 0) return null;
    const valid = points.filter(
      (p) => typeof p?.lat === 'number' && typeof p?.lng === 'number',
    );
    if (valid.length === 0) return null;
    const bounds = new google.maps.LatLngBounds();
    valid.forEach((p) => bounds.extend(new google.maps.LatLng(p.lat, p.lng)));
    return bounds;
  }, [google, points]);
}
